import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { ErrorState, Notice } from '@/components/ui/States'
import { Badge } from '@/components/ui/Badge'
import { supabase, describeError } from '@/lib/supabase'

/**
 * Enrolling an authenticator app, from the settings screen.
 *
 * Enrolment and enforcement are separate on purpose. Verifying a factor here
 * attaches it to the account; `mfa_required` on the staff row is what makes it
 * mandatory. Turning the flag on before a factor exists would lock the account
 * out of its own settings screen, so the toggle is only offered once a factor
 * is verified — and the flag is per-person, so the owner turning it on for
 * herself cannot lock out the assistant.
 */
export function MfaSetup({
  enrolled,
  required,
  onRequiredChange,
}: {
  enrolled: boolean
  required: boolean
  onRequiredChange: (next: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [stage, setStage] = useState<'idle' | 'enrolling'>('idle')
  const [factor, setFactor] = useState<{ id: string; qr: string; secret: string } | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function begin() {
    setBusy(true)
    setError(null)

    // A previous abandoned attempt leaves an unverified factor behind, and
    // Supabase refuses a second enrolment with the same friendly name.
    const existing = await supabase.auth.mfa.listFactors()
    const stale = existing.data?.all?.filter((item) => item.status === 'unverified') ?? []
    for (const item of stale) {
      await supabase.auth.mfa.unenroll({ factorId: item.id })
    }

    const { data, error: enrolError } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: `Authenticator ${new Date().toISOString().slice(0, 10)}`,
    })

    setBusy(false)

    if (enrolError || !data) {
      setError(describeError(enrolError))
      return
    }

    setFactor({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret })
    setStage('enrolling')
  }

  async function confirm(event: React.FormEvent) {
    event.preventDefault()
    if (!factor) return

    setBusy(true)
    setError(null)

    const challenge = await supabase.auth.mfa.challenge({ factorId: factor.id })
    if (challenge.error) {
      setError(describeError(challenge.error))
      setBusy(false)
      return
    }

    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId: factor.id,
      challengeId: challenge.data.id,
      code: code.trim(),
    })

    setBusy(false)

    if (verifyError) {
      setError('That code was not accepted. Codes change every 30 seconds — try the current one.')
      setCode('')
      return
    }

    setStage('idle')
    setFactor(null)
    setCode('')
    await queryClient.invalidateQueries()
    toast.success('Authenticator added')
  }

  async function remove() {
    setBusy(true)
    const { data } = await supabase.auth.mfa.listFactors()
    for (const item of data?.all ?? []) {
      await supabase.auth.mfa.unenroll({ factorId: item.id })
    }
    onRequiredChange(false)
    setBusy(false)
    await queryClient.invalidateQueries()
    toast.success('Authenticator removed')
  }

  if (stage === 'enrolling' && factor) {
    return (
      <div className="flex flex-col gap-4">
        <p className="measure text-[length:var(--text-sm)] text-taupe">
          Scan this with your authenticator app — 1Password, Authy, Google Authenticator, whichever you use — then enter
          the code it shows.
        </p>

        <div className="flex flex-wrap items-start gap-6">
          <img src={factor.qr} alt="QR code for authenticator enrolment" className="size-[168px] rounded-lg border bg-white p-2" />

          <div className="flex flex-col gap-1.5">
            <span className="label">Or type this key in by hand</span>
            <code className="max-w-[16rem] break-all border border-[var(--color-rule-strong)] px-2.5 py-2 text-[length:var(--text-xs)] tracking-wider">
              {factor.secret}
            </code>
          </div>
        </div>

        {error ? <ErrorState title="Could not add authenticator" detail={error} /> : null}

        <form onSubmit={confirm} className="flex flex-wrap items-end gap-3">
          <Field label="Code from the app" className="w-[9rem]">
            <Input
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              autoFocus
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              className="tracking-[0.3em]"
            />
          </Field>
          <Button type="submit" variant="primary" loading={busy} loadingLabel="Confirming" disabled={code.length !== 6}>
            Confirm
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setStage('idle')
              setFactor(null)
              setError(null)
            }}
          >
            Cancel
          </Button>
        </form>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {error ? <ErrorState title="Could not add authenticator" detail={error} /> : null}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Badge tone={enrolled ? 'sage' : 'amber'}>{enrolled ? 'Authenticator added' : 'No authenticator yet'}</Badge>
          <p className="measure text-[length:var(--text-sm)] text-taupe">
            {enrolled
              ? 'A code from your app is asked for at sign-in.'
              : 'Adds a second step at sign-in, so a stolen password on its own is not enough.'}
          </p>
        </div>

        {enrolled ? (
          <Button variant="danger" size="sm" loading={busy} loadingLabel="Removing" onClick={remove}>
            Remove authenticator
          </Button>
        ) : (
          <Button variant="primary" size="sm" loading={busy} loadingLabel="Starting" onClick={begin}>
            Add authenticator
          </Button>
        )}
      </div>

      {enrolled ? (
        <Notice
          tone={required ? 'sage' : 'amber'}
          title={required ? 'Two-step sign-in is enforced' : 'Two-step sign-in is optional'}
          action={
            <Button size="sm" variant={required ? 'secondary' : 'primary'} onClick={() => onRequiredChange(!required)}>
              {required ? 'Make optional' : 'Enforce it'}
            </Button>
          }
        >
          {required
            ? 'This account reads nothing until a code has been entered. That is the setting you want.'
            : 'Until this is enforced, a password alone still opens the studio. Turn it on now that your app is set up.'}
        </Notice>
      ) : null}
    </div>
  )
}
